import React, { useMemo, useState } from "react";
import { Clock, Film, Play, Youtube } from "lucide-react";
import { VIDEO_PLAYLISTS } from "../../data/videos";
import { Reveal, SectionHeading } from "../common";
import VideoModal from "../VideoModal";

export default function VideoGallery() {
  const [tab, setTab] = useState(VIDEO_PLAYLISTS[0].key);
  const [activeIndex, setActiveIndex] = useState(0);
  const [playing, setPlaying] = useState(null);

  const playlist = useMemo(
    () => VIDEO_PLAYLISTS.find((p) => p.key === tab) || VIDEO_PLAYLISTS[0],
    [tab]
  );
  const active = playlist.videos[Math.min(activeIndex, playlist.videos.length - 1)];

  const pick = (key) => {
    setTab(key);
    setActiveIndex(0);
  };

  const totalVideos = VIDEO_PLAYLISTS.reduce((n, p) => n + p.videos.length, 0);

  return (
    <section id="videos" className="bg-ivi-sand py-24 lg:py-32">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
        <SectionHeading
          eyebrow="Video gallery"
          title="Press play on your next chapter"
          text={`${totalVideos} short films shot on our projects turtles, classrooms, hospitals and the people who make it all work.`}
        />

        {/* Playlist tabs */}
        <Reveal className="mt-10 flex flex-wrap justify-center gap-2">
          <div className="flex flex-wrap justify-center gap-2">
            {VIDEO_PLAYLISTS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => pick(p.key)}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[14px] font-semibold transition-[background-color,color,border-color,transform] duration-200 hover:-translate-y-0.5 ${
                  tab === p.key
                    ? "bg-ivi-green text-white shadow-[0_10px_22px_-12px_rgba(90,158,47,1)]"
                    : "border border-ivi-mintDeep bg-white text-ivi-slate hover:border-ivi-green"
                }`}
              >
                <Film className="h-4 w-4" />
                {p.label}
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                    tab === p.key ? "bg-white/20 text-white" : "bg-ivi-mint text-ivi-greenDark"
                  }`}
                >
                  {p.videos.length}
                </span>
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 grid gap-6 lg:grid-cols-[1.55fr_1fr]">
          {/* Stage */}
          <Reveal>
            <button
              type="button"
              onClick={() => setPlaying(active)}
              className="group relative block w-full overflow-hidden rounded-[28px] bg-ivi-slate text-left shadow-[0_30px_70px_-38px_rgba(24,40,32,0.6)]"
            >
              <img
                key={active.id}
                src={active.poster}
                alt={active.title}
                className="aspect-video w-full object-cover transition-transform duration-[1100ms] group-hover:scale-105"
                style={{ animation: "heroUp 0.6s cubic-bezier(0.22,1,0.36,1) both" }}
              />
              <span className="absolute inset-0 bg-gradient-to-t from-ivi-slate/90 via-ivi-slate/15 to-ivi-slate/20" />
              <span className="absolute inset-0 grid place-items-center">
                <span className="grid h-24 w-24 place-items-center rounded-full bg-white/20 ring-1 ring-white/40 backdrop-blur-md transition-transform duration-300 group-hover:scale-110">
                  <span className="grid h-16 w-16 place-items-center rounded-full bg-ivi-green text-white">
                    <Play className="ml-1 h-7 w-7 fill-current" />
                  </span>
                </span>
              </span>
              <span className="absolute inset-x-7 bottom-7">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-white ring-1 ring-white/25 backdrop-blur">
                  <Youtube className="h-3.5 w-3.5" /> {playlist.label}
                </span>
                <span className="mt-3 block font-heading text-[22px] font-extrabold leading-snug text-white sm:text-[27px]">
                  {active.title}
                </span>
                <span className="mt-2 flex items-center gap-2 text-[13px] font-semibold text-white/70">
                  <Clock className="h-3.5 w-3.5" /> {active.duration} · Involvement Volunteers
                  International
                </span>
              </span>
            </button>
          </Reveal>

          {/* Playlist */}
          <Reveal delay={120}>
            <div className="flex h-full flex-col overflow-hidden rounded-[28px] bg-white shadow-[0_18px_46px_-32px_rgba(24,40,32,0.5)] ring-1 ring-ivi-mintDeep">
              <div className="flex items-center justify-between gap-3 border-b border-ivi-mint px-6 py-5">
                <div>
                  <p className="font-heading text-[16px] font-extrabold text-ivi-slate">
                    {playlist.label} playlist
                  </p>
                  <p className="mt-0.5 text-[13px] text-ivi-slateSoft">{playlist.blurb}</p>
                </div>
                <span className="shrink-0 rounded-full bg-ivi-mint px-3 py-1 font-heading text-[12px] font-bold text-ivi-greenDark">
                  {playlist.videos.length} videos
                </span>
              </div>

              <div className="no-scrollbar max-h-[430px] flex-1 overflow-y-auto p-3">
                {playlist.videos.map((vid, i) => {
                  const on = i === activeIndex;
                  return (
                    <button
                      key={vid.id + i}
                      type="button"
                      onClick={() => setActiveIndex(i)}
                      onDoubleClick={() => setPlaying(vid)}
                      className={`group flex w-full gap-4 rounded-2xl p-3 text-left transition-colors duration-200 ${
                        on ? "bg-ivi-mint" : "hover:bg-ivi-sand"
                      }`}
                    >
                      <span className="relative h-[58px] w-[104px] shrink-0 overflow-hidden rounded-xl">
                        <img
                          src={vid.poster}
                          alt={vid.title}
                          loading="lazy"
                          className="h-full w-full object-cover"
                        />
                        <span
                          className={`absolute inset-0 grid place-items-center transition-colors duration-200 ${
                            on ? "bg-ivi-slate/40" : "bg-ivi-slate/20 group-hover:bg-ivi-slate/40"
                          }`}
                        >
                          <span className="grid h-7 w-7 place-items-center rounded-full bg-white/90 text-ivi-greenDark">
                            <Play className="ml-0.5 h-3 w-3 fill-current" />
                          </span>
                        </span>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className={`block font-heading text-[14px] font-bold leading-snug ${
                            on ? "text-ivi-greenDark" : "text-ivi-slate"
                          }`}
                        >
                          {vid.title}
                        </span>
                        <span className="mt-1 block text-[12px] font-semibold text-ivi-slateSoft">
                          {vid.duration}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="border-t border-ivi-mint px-6 py-4">
                <button
                  type="button"
                  onClick={() => setPlaying(active)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ivi-green px-6 py-3 font-heading text-[14px] font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-ivi-greenDark"
                >
                  <Play className="h-4 w-4 fill-current" /> Play selected video
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <VideoModal
        open={!!playing}
        onOpenChange={(v) => !v && setPlaying(null)}
        videoId={playing?.id}
        title={playing?.title}
      />
    </section>
  );
}
